import { useState, useCallback, useEffect, useRef } from 'react'
import { useApi } from './useApi'
import { useCallHistoryStore } from '../stores/callHistoryStore'
import { useWebSocket } from './useWebSocket'
import type {
  CallHistoryRecord,
  Pagination,
  CallHistoryFetchParams,
  NoteSegment,
  NoteSegmentInput,
} from '../types'

interface UseCallHistoryOptions {
  customerId?: string | null
  limit?: number
  autoFetch?: boolean
  initialPage?: number
  enableRealtimeUpdates?: boolean
  isolatedState?: boolean
}

interface CallHistoryApiResponse {
  callHistory: CallHistoryRecord[]
  pagination?: Partial<Pagination>
}

interface NoteSegmentsResponse {
  noteSegments: NoteSegment[]
}

interface WsCallHistoryMessage {
  eventType?: string
  data?: Record<string, unknown>
}

/**
 * 通話履歴データを取得・管理するフック
 * CallHistoryPage, CustomerDetailModal, CustomerSidePanel で共通利用
 */
export function useCallHistory(options: UseCallHistoryOptions = {}) {
  const {
    customerId = null,
    limit = 20,
    autoFetch = true,
    initialPage = 1,
    enableRealtimeUpdates = true,
    isolatedState = false
  } = options

  const api = useApi()

  // Zustandストアから状態を取得
  const {
    callHistory,
    loading: storeLoading,
    error: storeError,
    pagination: storePagination,
    setCallHistory: setStoreCallHistory,
    setLoading: setStoreLoading,
    setError: setStoreError,
    setPagination: setStorePagination,
    setLastFetchParams,
    updateCallHistoryRecord,
    addCallHistoryRecord,
    patchCallHistoryField
  } = useCallHistoryStore()

  // ローカル状態（CustomerDetailModalなど独立したインスタンス用）
  const [localCallHistory, setLocalCallHistory] = useState<CallHistoryRecord[]>([])
  const [localLoading, setLocalLoading] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [localPagination, setLocalPagination] = useState<Pagination>({
    page: initialPage,
    limit,
    total: 0,
    totalPages: 0
  })

  // customerIdが指定されている場合、またはisolatedState=trueの場合はローカル状態を使用
  const useLocalState = !!customerId || isolatedState
  const currentCallHistory = useLocalState ? localCallHistory : callHistory
  const currentLoading = useLocalState ? localLoading : storeLoading
  const currentError = useLocalState ? localError : storeError
  const currentPagination = useLocalState ? localPagination : storePagination

  // 最後のフェッチパラメータを保持（WebSocket再接続時のリフレッシュ用）
  const lastParamsRef = useRef<CallHistoryFetchParams | null>(null)

  /**
   * 通話履歴を取得
   */
  const fetchCallHistory = useCallback(async (params: CallHistoryFetchParams = {}) => {
    try {
      if (useLocalState) {
        setLocalLoading(true)
        setLocalError(null)
      } else {
        setStoreLoading(true)
        setStoreError(null)
      }

      const queryParams = new URLSearchParams()

      // 顧客ID
      if (params.customerId || customerId) {
        queryParams.append('customerId', (params.customerId || customerId)!)
      }

      // ページネーション
      const page = params.page || (useLocalState ? localPagination.page : storePagination.page)
      queryParams.append('page', String(page))
      queryParams.append('limit', String(params.limit || limit))

      // 日付範囲
      if (params.startDate) {
        queryParams.append('startDate', params.startDate)
      }
      if (params.endDate) {
        queryParams.append('endDate', params.endDate)
      }

      // フィルター
      if (params.callType) {
        queryParams.append('callType', params.callType)
      }
      if (params.myCallsOnly) {
        queryParams.append('myCallsOnly', 'true')
      }

      // ソート
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy)
      }
      if (params.sortOrder) {
        queryParams.append('sortOrder', params.sortOrder)
      }

      const data = await api.get<CallHistoryApiResponse>(`/api/call-history?${queryParams.toString()}`)

      const paginationData: Pagination = {
        page: data.pagination?.page || 1,
        limit: data.pagination?.limit || limit,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0
      }

      if (useLocalState) {
        setLocalCallHistory(data.callHistory || [])
        setLocalPagination(paginationData)
      } else {
        setStoreCallHistory(data.callHistory || [])
        setStorePagination(paginationData)
        setLastFetchParams(params)
      }

      // リフレッシュ用にパラメータを保持
      lastParamsRef.current = params

      return data
    } catch (err) {
      console.error('Failed to fetch call history:', err)
      const errorMessage = (err as Error).message || '通話履歴の取得に失敗しました'
      if (useLocalState) {
        setLocalError(errorMessage)
      } else {
        setStoreError(errorMessage)
      }
      throw err
    } finally {
      if (useLocalState) {
        setLocalLoading(false)
      } else {
        setStoreLoading(false)
      }
    }
  }, [api, customerId, limit, useLocalState, localPagination.page, storePagination.page,
      setStoreCallHistory, setStoreLoading, setStoreError, setStorePagination, setLastFetchParams])

  /**
   * 特定の通話履歴を取得
   */
  const fetchCallHistoryById = useCallback(async (callHistoryId: string) => {
    try {
      const data = await api.get<CallHistoryRecord>(`/api/call-history/${callHistoryId}`)
      return data
    } catch (err) {
      console.error('Failed to fetch call history by id:', err)
      throw err
    }
  }, [api])

  /**
   * NoteSegmentsを取得
   */
  const fetchNoteSegments = useCallback(async (callHistoryId: string): Promise<NoteSegment[]> => {
    try {
      const data = await api.get<NoteSegmentsResponse>(`/api/call-history/${callHistoryId}/note-segments`)
      return data.noteSegments || []
    } catch (err) {
      console.error('Failed to fetch note segments:', err)
      throw err
    }
  }, [api])

  /**
   * NoteSegmentを作成
   */
  const createNoteSegment = useCallback(async (callHistoryId: string, noteData: NoteSegmentInput) => {
    try {
      const data = await api.post<NoteSegment>(`/api/call-history/${callHistoryId}/note-segments`, {
        note: noteData.note || '',
        callType: noteData.callType || '',
        summary: noteData.summary || '',
        tags: noteData.tags || [],
        clientCreatedAt: new Date().toISOString()
      })
      return data
    } catch (err) {
      console.error('Failed to create note segment:', err)
      throw err
    }
  }, [api])

  /**
   * NoteSegmentを更新
   */
  const updateNoteSegment = useCallback(async (callHistoryId: string, noteSegmentId: string, noteData: Partial<NoteSegmentInput>) => {
    try {
      const data = await api.put<NoteSegment>(`/api/call-history/${callHistoryId}/note-segments/${noteSegmentId}`, {
        note: noteData.note,
        callType: noteData.callType,
        summary: noteData.summary,
        tags: noteData.tags
      })
      return data
    } catch (err) {
      console.error('Failed to update note segment:', err)
      throw err
    }
  }, [api])

  /**
   * NoteSegmentを削除
   */
  const deleteNoteSegment = useCallback(async (callHistoryId: string, noteSegmentId: string) => {
    try {
      await api.delete(`/api/call-history/${callHistoryId}/note-segments/${noteSegmentId}`)
    } catch (err) {
      console.error('Failed to delete note segment:', err)
      throw err
    }
  }, [api])

  /**
   * 通話履歴を更新（メタデータ: callType, satisfaction など）
   */
  const updateCallHistory = useCallback(async (callHistoryId: string, updateData: Partial<CallHistoryRecord>) => {
    try {
      const data = await api.put<CallHistoryRecord>(`/api/call-history/${callHistoryId}`, updateData)
      return data
    } catch (err) {
      console.error('Failed to update call history:', err)
      throw err
    }
  }, [api])

  /**
   * データをリフレッシュ
   */
  const refresh = useCallback(() => {
    return fetchCallHistory(lastParamsRef.current || {})
  }, [fetchCallHistory])

  /**
   * ページを変更
   */
  const setPage = useCallback((newPage: number) => {
    return fetchCallHistory({ ...lastParamsRef.current, page: newPage })
  }, [fetchCallHistory])

  /**
   * WebSocketメッセージハンドラ
   */
  const handleWebSocketMessage = useCallback((message: WsCallHistoryMessage) => {
    // 顧客別表示の場合は対象外
    if (useLocalState) return

    // call-history関連イベントのみログ
    if (message.eventType?.startsWith('call-history.')) {
      console.log('[useCallHistory] WS受信:', message.eventType,
        'data=', JSON.stringify(message.data).substring(0, 200))
    }

    // 通話履歴作成イベント
    if (message.eventType === 'call-history.created') {
      const record = message.data as unknown as CallHistoryRecord
      if (record) {
        addCallHistoryRecord(record)
      }
    }

    // 通話履歴更新イベント
    if (message.eventType === 'call-history.updated') {
      const data = message.data || {}
      const callHistoryId = data.callHistoryId as string | undefined
      if (callHistoryId) {
        const { callHistoryId: _, ...updateData } = data
        updateCallHistoryRecord(callHistoryId, updateData as Partial<CallHistoryRecord>)
      }
    }

    // 特定フィールドのみ更新（軽量な部分更新）
    if (message.eventType === 'call-history.field-updated') {
      const data = message.data || {}
      const callHistoryId = data.callHistoryId as string | undefined
      const field = data.field as string | undefined
      const value = data.value
      if (callHistoryId && field) {
        patchCallHistoryField(callHistoryId, field, value)
      }
    }
  }, [useLocalState, addCallHistoryRecord, updateCallHistoryRecord, patchCallHistoryField])

  /**
   * WebSocket再接続時のリフレッシュ
   */
  const handleReconnect = useCallback(() => {
    if (!useLocalState && lastParamsRef.current) {
      console.log('[useCallHistory] WebSocket再接続 - データをリフレッシュ')
      refresh()
    }
  }, [useLocalState, refresh])

  // WebSocket接続（リアルタイム更新が有効な場合のみ）
  useWebSocket(
    enableRealtimeUpdates && !useLocalState ? handleWebSocketMessage : undefined,
    enableRealtimeUpdates && !useLocalState ? handleReconnect : undefined
  )

  // 自動取得
  useEffect(() => {
    if (autoFetch && customerId) {
      fetchCallHistory()
    }
  }, [autoFetch, customerId, fetchCallHistory])

  return {
    // データ
    callHistory: currentCallHistory,
    loading: currentLoading,
    error: currentError,
    pagination: currentPagination,

    // 取得関数
    fetchCallHistory,
    fetchCallHistoryById,
    fetchNoteSegments,

    // NoteSegments操作
    createNoteSegment,
    updateNoteSegment,
    deleteNoteSegment,

    // 通話履歴更新
    updateCallHistory,

    // ユーティリティ
    refresh,
    setPage,
    setCallHistory: useLocalState ? setLocalCallHistory : setStoreCallHistory,
    setError: useLocalState ? setLocalError : setStoreError,

    // ストアの直接操作（高度な使用向け）
    updateCallHistoryRecord: useLocalState ? undefined : updateCallHistoryRecord,
    addCallHistoryRecord: useLocalState ? undefined : addCallHistoryRecord,
    patchCallHistoryField: useLocalState ? undefined : patchCallHistoryField
  }
}
